/**
 * Navigation structure of the settings area.
 *
 * Kept as data so that the sidebar, the page title and the content switch all
 * read from one place instead of repeating the list three times.
 */
export interface SettingsNavItem {
  /** Value of the `tab` query param — also identifies the rendered panel. */
  param: string;
  label: string;
  /** Alternative label for network members, who see a reduced feature set. */
  memberLabel?: string;
  /** Alternative label for a network (Zentrale), e.g. where it lacks redemption. */
  networkLabel?: string;
  /** Lucide icon name; sub items have none. */
  icon?: string;
  /** Hidden from group members. */
  memberHidden?: boolean;
  /** Only for members and network Zentralen (e.g. the network bank account). */
  verbundOnly?: boolean;
  /** Only for plain shops: hidden from members and network Zentralen. */
  shopOnly?: boolean;
  /** Only for the account's owner: hidden while an agency works in the account. */
  ownerOnly?: boolean;
  children?: SettingsNavItem[];
}

export interface SettingsNavGroup {
  label: string;
  items: SettingsNavItem[];
}

export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    label: 'Einstellungen',
    items: [
      {
        param: 'sell-settings',
        label: 'Gutschein-App',
        icon: 'lucideTicket',
        memberHidden: true,
      },
      {
        param: 'voucher-design',
        label: 'Gutschein-Design',
        icon: 'lucidePalette',
        memberHidden: true,
      },
      {
        param: 'storefront',
        label: 'Deine Schenkwerk-Webseite',
        icon: 'lucideStore',
        memberHidden: true,
        children: [
          { param: 'storefront', label: 'Shop-Einstellungen' },
          { param: 'qr-code', label: 'Zugang per QR-Code' },
        ],
      },
      {
        // Members only get the PIN card inside this page, the fee card is hidden
        // for them — so the entry itself stays visible. A network redeems nothing,
        // so it only gets the fee card.
        param: 'fees-redemption',
        label: 'Gebühren & Einlösung',
        memberLabel: 'PIN Einlösung',
        networkLabel: 'Gebühren',
        icon: 'lucidePercent',
      },
      {
        // Networks have their own fees and members sell nothing: plain shops only.
        param: 'recommend',
        label: 'Schenkwerk empfehlen',
        icon: 'lucideGift',
        shopOnly: true,
      },
      {
        param: 'integration',
        label: 'Integration',
        icon: 'lucideCode',
      },
      {
        param: 'verbund',
        label: 'Netzwerk',
        icon: 'lucideUsers',
        children: [
          { param: 'verbund', label: 'Netzwerk-Einstellungen', memberHidden: true },
          { param: 'verbund-members', label: 'Netzwerk-Mitglieder' },
          {
            param: 'verbund-settlements',
            label: 'Abrechnungen',
            verbundOnly: true,
            memberHidden: true,
          },
        ],
      },
      {
        param: 'notifications',
        label: 'Benachrichtigungen',
        icon: 'lucideBell',
        memberHidden: true,
      },
    ],
  },
  {
    label: 'Konto',
    items: [
      {
        // Members have no Stripe account: the network pays them by bank transfer.
        param: 'payments',
        label: 'Zahlungsinformationen',
        icon: 'lucideCreditCard',
        memberHidden: true,
      },
      {
        // Who may work in the account and what they changed. An agency can't manage
        // its own access, so it doesn't see the entry.
        param: 'partner-access',
        label: 'Deine Agentur',
        icon: 'lucideHandshake',
        shopOnly: true,
        ownerOnly: true,
      },
      {
        param: 'company',
        label: 'Account',
        icon: 'lucideUser',
        children: [
          { param: 'company', label: 'Allgemein' },
          { param: 'bank-account', label: 'Bankverbindung', verbundOnly: true },
        ],
      },
      {
        // Documents for the merchant's tax adviser. Members don't sell: their network
        // pays them, see /payouts.
        param: 'accounting-statement',
        label: 'Belege',
        icon: 'lucideFileText',
        memberHidden: true,
        children: [
          { param: 'accounting-statement', label: 'Monatsabrechnung', memberHidden: true },
          { param: 'payout-statements', label: 'Auszahlungsbelege', memberHidden: true },
          // "invoice" stays the param, so existing links keep working.
          { param: 'invoice', label: 'Gebührenrechnungen', memberHidden: true },
        ],
      },
    ],
  },
];

/** Every param that can appear in the URL, including sub items. */
export function findNavItem(param: string): SettingsNavItem | undefined {
  for (const group of SETTINGS_NAV) {
    for (const item of group.items) {
      const child = item.children?.find((sub) => sub.param === param);
      if (child) return child;
      if (!item.children && item.param === param) return item;
    }
  }
  return undefined;
}

export interface NavAudience {
  isMember: boolean;
  isNetwork: boolean;
  /** An agency works in the account (AuthService.actingPartnerName). */
  isActingPartner?: boolean;
}

/** One rule for the sidebar, the landing tab and the page title. */
export function isNavItemVisible(item: SettingsNavItem, who: NavAudience): boolean {
  if (who.isMember && item.memberHidden) return false;
  if (item.verbundOnly && !(who.isMember || who.isNetwork)) return false;
  if (item.shopOnly && (who.isMember || who.isNetwork)) return false;
  if (item.ownerOnly && who.isActingPartner) return false;
  return true;
}

/** The label an item shows to this audience (members and networks may see their own). */
export function navLabel(
  item: SettingsNavItem,
  audience: { isMember: boolean; isNetwork: boolean },
): string {
  if (audience.isMember && item.memberLabel) return item.memberLabel;
  if (audience.isNetwork && item.networkLabel) return item.networkLabel;
  return item.label;
}
